/**
 * Time Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de tempo/duração.
 *
 * Fontes:
 * - SI Brochure, 9th Edition (2019)
 * - Gregorian calendar (365.2425 dias/ano)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type TimeUnit =
  // SI prefixes
  | "nanosecond"
  | "microsecond"
  | "millisecond"
  | "second"
  // Common
  | "minute"
  | "hour"
  | "day"
  | "week"
  // Calendar (approximate)
  | "month"
  | "year"
  | "decade"
  | "century"
  | "millennium";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de tempo.
 *
 * Fatores exatos:
 * - SI: prefixos são exatos por definição
 * - day, hour, minute: exatos (86400s, 3600s, 60s)
 * - week: exato (7 dias = 604800s)
 *
 * Fatores aproximados (baseados no calendário Gregoriano):
 * - year: 365.2425 dias (média do ciclo de 400 anos)
 * - month: year/12
 */
export const TIME_UNITS: Record<TimeUnit, SimpleUnitConfig> = {
  // SI prefixes (exatos)
  nanosecond: {
    factor: 1e-9,
    symbol: "ns",
    singular: "nanosecond",
    plural: "nanoseconds",
  },
  microsecond: {
    factor: 1e-6,
    symbol: "μs",
    singular: "microsecond",
    plural: "microseconds",
  },
  millisecond: {
    factor: 0.001,
    symbol: "ms",
    singular: "millisecond",
    plural: "milliseconds",
  },
  second: {
    factor: 1,
    symbol: "s",
    singular: "second",
    plural: "seconds",
  },

  // Common (exatos)
  minute: {
    factor: 60,
    symbol: "min",
    singular: "minute",
    plural: "minutes",
  },
  hour: {
    factor: 3600,
    symbol: "h",
    singular: "hour",
    plural: "hours",
  },
  day: {
    factor: 86400,
    symbol: "d",
    singular: "day",
    plural: "days",
  },
  week: {
    factor: 604800,
    symbol: "wk",
    singular: "week",
    plural: "weeks",
  },

  // Calendar (aproximados - baseados no ano Gregoriano)
  month: {
    factor: 2629746, // 31556952 / 12
    symbol: "mo",
    singular: "month",
    plural: "months",
  },
  year: {
    factor: 31556952, // 365.2425 * 86400
    symbol: "yr",
    singular: "year",
    plural: "years",
  },
  decade: {
    factor: 315569520, // 10 * year
    symbol: "dec",
    singular: "decade",
    plural: "decades",
  },
  century: {
    factor: 3155695200, // 100 * year
    symbol: "c",
    singular: "century",
    plural: "centuries",
  },
  millennium: {
    factor: 31556952000, // 1000 * year
    symbol: "ky",
    singular: "millennium",
    plural: "millennia",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const TIME_ALIASES: UnitAliases = {
  // nanosecond
  ns: "nanosecond",
  // microsecond
  "μs": "microsecond",
  us: "microsecond",
  // millisecond
  ms: "millisecond",
  // second
  s: "second",
  sec: "second",
  // minute
  min: "minute",
  // hour
  h: "hour",
  hr: "hour",
  hrs: "hour",
  // day
  d: "day",
  // week
  wk: "week",
  // month
  mo: "month",
  // year
  yr: "year",
  y: "year",
  // decade
  dec: "decade",
  // century
  c: "century",
  // millennium
  ky: "millennium",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const TIME_UNIT_LIST = Object.keys(TIME_UNITS) as TimeUnit[];

/** Fatores de conversão (para compatibilidade) */
export const TIME_FACTORS: Record<TimeUnit, number> = Object.fromEntries(
  Object.entries(TIME_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<TimeUnit, number>;
